import React, { useEffect, useState, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { importJson, jsonArrayToHtml, handleHotkey } from './functions';
import SideBar from './sidebar';
import { BACKEND_URL } from '../global';
import Layout from '../components/Layout';
import keycloak from '../keycloak';
import ReactDOM from 'react-dom/client';
import { transcribeFile } from '../file-browser/functions';
import { TranscriptionWebSocketContext } from '../transcriptionWssProvider';
import { SidebarProvider, SidebarTrigger, Button, Tooltip, TooltipTrigger, TooltipContent, Label } from '../shadcnComponents/ui';
import { Undo, Redo } from 'lucide-react';

function Editor() {
  const navigate = useNavigate();
  const location = useLocation();
  const webSocket = useContext(TranscriptionWebSocketContext)
  const { state } = location;
  const transcriptionRootElement = useRef(null)
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [interviewTitle, setInterviewTitle] = useState(null)
  const [speakerNameToRename, setSpeakerNameToRename] = useState(null)
  const speakerNamesToRename = useRef([])

  useEffect(() => {
    if (state == null || state.type == null) {
      navigate('/home');
    } else {
      loadTranscriptionFromDatabase()
      document.addEventListener("keydown", handleHotkey);
    }
    return () => {
      document.removeEventListener("keydown", handleHotkey);
    };
  }, []);


  // Fetch transcription when transcribing is over
  useEffect(() => {
    if (webSocket.messageState && webSocket.messageState.type == 'transcription_status') {
      if (webSocket.messageState.interview_id == localStorage.getItem('interviewId') ? localStorage.getItem('interviewId') : state.id) {
        loadTranscriptionFromDatabase()
      }
    }
  }, [webSocket])

  useEffect(() => {
    if (speakerNameToRename != null) {
      // Remove existing input
      hideSpeakerNameInput()
      // Make an array of all elements to update
      let tempSpeakerNameArray = []
      Array.from(document.getElementById('hypertranscript').getElementsByClassName('speaker')).map((each) => {
        // Add event listener for renaming on speaker name or for triggering play audio for any word
        if (each.innerText == speakerNameToRename.innerText) {
          tempSpeakerNameArray.push(each)
        }
      })
      speakerNamesToRename.current = tempSpeakerNameArray
      let input = document.createElement('input')
      input.setAttribute('class', 'w-fit placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input focus:border-buttonBorder-accent focus:border-2 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm')
      input.setAttribute("id", "newSpeakerName")
      input.setAttribute("data-m", speakerNameToRename.getAttribute("data-m"))
      input.placeholder = speakerNameToRename.innerText
      input.onkeyup = changeSpeakerName
      speakerNameToRename.replaceWith(input)
      document.getElementById("newSpeakerName").focus()
      document.addEventListener('mousedown', handleClickAnywhere)
    }
  }, [speakerNameToRename])

  function hideSpeakerNameInput() {
    speakerNamesToRename.current = []
    let inputElement = document.getElementById("newSpeakerName")
    if (inputElement) {
      let name = inputElement.value.trim() != "" ? inputElement.value : inputElement.placeholder
      const speakerElement = document.createElement('span');
      speakerElement.setAttribute('class', 'text-[rgb(102,102,102)] font-bold cursor-pointer speaker');
      speakerElement.setAttribute('data-m', inputElement.getAttribute("data-m"));
      speakerElement.innerText = name;
      speakerElement.addEventListener('click', (e) => {
        setSpeakerNameToRename(e.target)
      })
      inputElement.replaceWith(speakerElement)
    }
  }

  function changeSpeakerName(e) {
    // Check if input contains only spaces
    if (e.target.value.trim() != "") {
      speakerNamesToRename.current.map(each => {
        each.innerText = e.target.value
      })
    }
  }

  function handleClickAnywhere(event) {
    const inputElement = document.getElementById("newSpeakerName")
    if (!inputElement.contains(event.target) && inputElement != event.target) {
      hideSpeakerNameInput()
      document.removeEventListener('mousedown', handleClickAnywhere)
    }
  }

  function unsavedTrigger(e) {
    e.preventDefault();
  }

  function setChangeListener(set) {
    if (set) {
      window.addEventListener('beforeunload', unsavedTrigger);
    } else {
      window.removeEventListener('beforeunload', unsavedTrigger);
    }
  }

  function addTranscriptEventLiteners() {
    document.getElementById('interview-placeholder').classList.remove('block');
    document.getElementById('interview-placeholder').classList.add('hidden');
    document.getElementById('hypertranscript').classList.add('flex');
    document.getElementById('hypertranscript').classList.remove('hidden');
    Array.from(document.getElementById('hypertranscript').querySelectorAll('[data-m]')).map((each) => {
      // Add event listener for renaming on speaker name or for triggering play audio for any word
      if (each.classList.contains('speaker')) {
        each.addEventListener('click', (e) => {
          setSpeakerNameToRename(e.target)
        })
      } else {
        each.addEventListener('dblclick', (e) => {
          if (document.getElementById('audio-player').src != '') {
            document.getElementById('audio-player').currentTime = Math.floor(e.target.getAttribute('data-m') / 1000);
            document.getElementById('audio-player').play();
          }
        });
      }
    });
  }

  function loadTranscriptionFromDatabase() {
    let id = localStorage.getItem('interviewId') ? localStorage.getItem('interviewId') : state.id
    fetch(`${BACKEND_URL}/asr_results/${id}`, {
      headers: {
        Authorization: `Bearer ${keycloak.token}`
      }
    })
      .then((res) => res.json())
      .then((r) => {
        // Interview ID in greek may cause errors on fetch
        fetch(`${BACKEND_URL}/audio/?interview_id=${r.interview_id}`, {
          headers: {
            Authorization: `Bearer ${keycloak.token}`
          }
        })
          .then(
            (response) => {
              if (response.status != 200 && response.status != 404) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              } else if (response.status == 200) {
                return response.blob();
              } else {
                return false
              }
            },
            (error) => {
              console.error('Error fetching audio:', error);
            },
          ).then((audioBlob) => {
            if (audioBlob && document.getElementById('audio-player')) {
              const audioURL = URL.createObjectURL(audioBlob);
              const audioElement = document.getElementById('audio-player');

              // Set the audio element's source to the object URL
              audioElement.src = audioURL;

              // Load the audio file into the player
              audioElement.load();
              // Release the object URL once the audio element no longer needs it
              audioElement.onended = () => {
                URL.revokeObjectURL(audioURL);
              };
            }
          });

        setInterviewTitle(r.interview_id)
        if (r.annotation && Object.keys(r.annotation).length != 0) {
          importJson(r.annotation);
          addTranscriptEventLiteners()
        } else if (r.transcript && r.transcript.length > 0) {
          document.getElementById('hypertranscript').innerHTML = jsonArrayToHtml(r.transcript);
          addTranscriptEventLiteners()
        } else {
          if (transcriptionRootElement.current == null) {
            transcriptionRootElement.current = ReactDOM.createRoot(document.getElementById('interview-placeholder'))
          }
          transcriptionRootElement.current.render(
            <div className='text-2xl w-full h-[500px] max-h-full flex flex-col gap-4'>
              <Label className='mx-auto text-lg mt-auto'>
                Το αρχείο κειμένου είναι κενό
              </Label>
              <Button variant="no" className="w-fit mx-auto mb-auto" onClick={() => { transcribeFile(localStorage.getItem('interviewId') ? localStorage.getItem('interviewId') : state.id) }}>
                Δημιουργία απομαγνητοφώνησης
              </Button>

            </div>
          )

        }
      });
  }

  return (
    <Layout authenticated={true} sidebar={false}>
      <SidebarProvider className='h-full' style={{
        "--sidebar-width": "24rem",
        "--sidebar-width-mobile": "24rem",
      }}>
        <SideBar title={interviewTitle} unsetChangeListener={() => { setChangeListener(false) }} interviewId={localStorage.getItem('interviewId') ? localStorage.getItem('interviewId') : state.id} />
        <div className={`flex flex-col ${sidebarVisible ? 'w-[calc(100%-24rem)]' : 'w-full  delay-150'} px-5 h-full pb-10 ml-auto transition-[width] duration-200 ease-out`}>
          <div className='w-full pt-4 pb-5 flex flex-row items-center justify-start'>
            <SidebarTrigger onClick={() => { setSidebarVisible(!sidebarVisible) }} />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="lg" className='size-7 ml-4' onClick={() => { document.execCommand('undo', false, null) }}><Undo className="size-6" /></Button>
              </TooltipTrigger>
              <TooltipContent side={'bottom'}>Αναίρεση</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="lg" className='size-7' onClick={() => { document.execCommand('redo', false, null) }}><Redo className="size-6" /></Button>
              </TooltipTrigger>
              <TooltipContent side={'bottom'}>Επανάληψη</TooltipContent>
            </Tooltip>
          </div>
          <div className="w-full flex flex-row items-center justify-center overflow-auto">
            <div id="hypertranscript" onInput={() => { setChangeListener(true); }} contentEditable className="w-full hidden h-full overflow-visible outline-none text-[13pt]" />
            <div id="interview-placeholder" className="block w-full text-center font-bold text-gray-600">Φορτώστε μία συνέντευξη</div>
          </div>
        </div>
      </SidebarProvider>
    </Layout>
  );
}

export default Editor;
